import React, { useState, useEffect, useCallback, useRef } from 'react';
import alphabetData from '../Data/alphabetData';
import Confetti from 'react-confetti';
import { shuffle } from '../utils/shuffle';
import { getRandomMascotQuote } from '../utils/mascotQuotes';
import useAudio from '../hooks/useAudio';
import MascotCelebration from './MascotCelebration';
import ScoreBadge from './ScoreBadge';

const TOTAL_ROUNDS = 6;

const SoundMatching = () => {
  const [gameWords, setGameWords] = useState([]);
  const [round, setRound] = useState(0);
  const [options, setOptions] = useState([]);
  const [correctWord, setCorrectWord] = useState(null);
  const [score, setScore] = useState(0);
  const [message, setMessage] = useState('');
  const [showConfetti, setShowConfetti] = useState(false);
  const [showMascot, setShowMascot] = useState(false);
  const [mascotQuote, setMascotQuote] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);

  const gridRef = useRef(null);
  const { play } = useAudio();

  const initializeGame = () => {
    const allValidWords = [];

    alphabetData.forEach(letter => {
      letter.words?.forEach(wordObj => {
        if (wordObj.games?.includes('SoundMatching')) {
          allValidWords.push({
            word: wordObj.word,
            imageFile: wordObj.imageFile,
            soundFile: wordObj.soundFile
          });
        }
      });
    });

    const selectedWords = shuffle(allValidWords).slice(0, TOTAL_ROUNDS);
    setGameWords(selectedWords);
    setRound(0);
    setScore(0);
    setShowConfetti(false);
    setShowMascot(false);
    setMascotQuote('');
    setSelectedImage(null);
  };

  const setupRound = useCallback(() => {
    if (round >= TOTAL_ROUNDS) return;

    const current = gameWords[round];
    setCorrectWord(current);

    const others = shuffle(gameWords.filter(w => w.word !== current.word)).slice(0, 3);

    const optionSet = shuffle([...others, current]);
    setOptions(optionSet);
    setSelectedImage(null);
    play(current.soundFile);
  }, [round, gameWords, play]);

  useEffect(() => {
    initializeGame();
  }, []);

  useEffect(() => {
    if (gameWords.length === TOTAL_ROUNDS) {
      setupRound();
    }
  }, [gameWords, round, setupRound]);

  useEffect(() => {
    const grid = gridRef.current;
    if (!grid) return;

    const computeSize = () => {
      const popup = grid.closest('.game-popup-content');
      const popupRect = popup ? popup.getBoundingClientRect() : null;
      const gridRect = grid.getBoundingClientRect();
      const styles = getComputedStyle(grid);
      const gap = parseFloat(styles.gap || styles.rowGap || '0');
      const paddingX = parseFloat(styles.paddingLeft || '0') + parseFloat(styles.paddingRight || '0');
      const paddingY = parseFloat(styles.paddingTop || '0') + parseFloat(styles.paddingBottom || '0');

      const cols = 2;
      const rows = 2;
      const totalGapX = gap * (cols - 1);
      const totalGapY = gap * (rows - 1);

      const hostWidth = popup ? popup.clientWidth : window.innerWidth;
      const widthAvailable = Math.max(0, hostWidth - paddingX - totalGapX);
      const widthBased = widthAvailable / cols;

      let heightAvailable;
      if (popupRect) {
        heightAvailable = popupRect.bottom - gridRect.top - paddingY;
      } else {
        heightAvailable = window.innerHeight - gridRect.top - paddingY - 24;
      }
      const heightBased = Math.max(0, heightAvailable - totalGapY) / rows;

      const card = Math.max(100, Math.min(widthBased, heightBased));
      grid.style.setProperty('--sm-card', `${card}px`);
    };

    computeSize();
    const raf = requestAnimationFrame(() => requestAnimationFrame(computeSize));
    const t1 = setTimeout(computeSize, 250);
    const t2 = setTimeout(computeSize, 600);

    window.addEventListener('resize', computeSize);
    window.addEventListener('orientationchange', computeSize);

    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(computeSize);
      ro.observe(grid);
      const popup = grid.closest('.game-popup-content');
      if (popup) ro.observe(popup);
    }

    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(t1);
      clearTimeout(t2);
      window.removeEventListener('resize', computeSize);
      window.removeEventListener('orientationchange', computeSize);
      if (ro) ro.disconnect();
    };
  }, []);

  const handleChoice = (item) => {
    if (selectedImage) return;

    const isCorrect = item.word === correctWord.word;
    setSelectedImage({ word: item.word, correct: isCorrect });

    play(isCorrect ? 'correct.mp3' : 'wrong.mp3');

    if (isCorrect) {
      setScore(prev => prev + 1);
    }

    setTimeout(() => {
      setMessage('');
      if (isCorrect) {
        if (round + 1 === TOTAL_ROUNDS) {
          setMascotQuote(getRandomMascotQuote());
          setShowConfetti(true);
          setShowMascot(true);
          play('fanfare.mp3');
        } else {
          setRound(prev => prev + 1);
        }
      } else {
        setSelectedImage(null);
      }
    }, 1500);
  };

  const handleReplaySound = () => {
    if (correctWord?.soundFile) {
      play(correctWord.soundFile);
    }
  };

  const handleRestart = () => {
    initializeGame();
  };

  return (
    <div className="game-popup-content" onClick={e => e.stopPropagation()}>
      <ScoreBadge score={score} />

      <div className="sm-message">{message}</div>

      <div className="sm-side-controls">
        <button onClick={handleReplaySound} className="sm-replay-button">🔊</button>
        <div className="sm-round-counter">سوال: {round + 1} / {TOTAL_ROUNDS}</div>
      </div>

      <div className="sm-options-grid">
        {options.map((item, idx) => (
          <div
            key={idx}
            onClick={() => handleChoice(item)}
            style={{
              border: selectedImage?.word === item.word
                ? selectedImage.correct
                  ? '4px solid green'
                  : '4px solid red'
                : '2px solid black',
              borderRadius: '12px',
              padding: '0px',
              transition: 'border 0.2s ease-in-out',
              cursor: 'pointer',
              backgroundColor: '#fff'
            }}
          >
            <img
              src={`${process.env.PUBLIC_URL}/images/${item.imageFile}`}
              alt={item.word}
              className="sm-option-img"
            />
          </div>
        ))}
      </div>

      {showMascot && (
        <MascotCelebration quote={mascotQuote} onReplay={handleRestart} />
      )}

      {showConfetti && <Confetti />}
    </div>
  );
};

export default SoundMatching;
